import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DropzoneArea } from "mui-file-dropzone";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import CircularProgress from "@mui/material/CircularProgress";
import * as Colors from "@mui/material/colors";

import {
  uploadDocumentRequest,
  uploadClear
} from "../../../actions/uploadFileActions";
import { notificationOpen } from "../../../actions/notificationActions";
import ArgoButtonHook from "../../common/ArgoButtonHook";

/* ────────────────────────────────────────────────────────── */
/* constant styles keep-as-is                                 */
/* ────────────────────────────────────────────────────────── */
const QuestionnaireImportStyles = {
  root: {
    width: 500,
    height: 220,
    border: "2px solid #039BE5",
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: "5px",
    paddingTop: "5px"
  },
  active: {
    border: "2px dashed #039BE5"
  },
  rejected: {
    border: "2px dashed #E53935"
  },
  accepted: {
    border: "2px dashed #7CB342"
  }
};

const batchResultsStyles = {
  wrapperStyle: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%"
  },
  list: { padding: 0 },
  messageList: { listStyleType: "none" }
};

/* ────────────────────────────────────────────────────────── */
/* main component                                             */
/* ────────────────────────────────────────────────────────── */
const QuestionnaireImportHook = () => {
  const dispatch = useDispatch();
  const uploadedFiles = useSelector((state) => state.uploadedFiles.items);

  const [files, setFiles] = useState([]);
  const [disableImport, setDisableImport] = useState(true)
  const [uploading, setUploading] = useState(false);

  /* stop spinner when new result batch arrives */
  useEffect(() => {
    setUploading(false);
  }, [uploadedFiles]);

  /* add dropped files (max 10) */
  const handleChange = useCallback(
    (incoming) => {
      let totalFileSizes = incoming.reduce((sum, file) => file.size + sum, 0)
      let importDisable = incoming.length === 0

      if (totalFileSizes >= (10 * 1024 * 1024)) {
        importDisable = true
        dispatch(
            notificationOpen(
                "Questionnaire import is limited to a total size of 10 mb at a time."
            )
        );
      }

      setFiles(incoming)
      setDisableImport(importDisable)
    },
    [dispatch]
  );

  /* remove one chip */
  const onDeleteChip = useCallback((fileToDelete) => {
    setFiles((prev) => prev.filter((f) => f.name !== fileToDelete.name));
  }, []);

  /* upload */
  const handleImport = useCallback(() => {
    const data = new FormData();
    files.forEach((f) => data.append("files", f));

    dispatch(uploadDocumentRequest(data));
    setFiles([]);
    setUploading(true);
  }, [dispatch, files]);

  /* clear */
  const handleClear = useCallback(() => {
    setFiles([]);
    dispatch(uploadClear());
  }, [dispatch]);

  /* newest first */
  const uploadedFilesReversed = [...uploadedFiles].reverse();

  /* ──────────────────────────────────────────────────────── */
  return (
    <div
      id="QuestionnaireImport-container-div"
      style={{ height: "100%", padding: "15px", marginTop: "64px" }}
    >
      <h2 className="primaryText">Import Questionnaire</h2>

      {/* DROPZONE */}
      {uploading ? (
        <CircularProgress size={250} />
      ) : (
        <div id="QuestionnaireImport-DropzoneArea-container-div">
          <DropzoneArea
            id="QuestionnaireImport-dropzone"
            style={QuestionnaireImportStyles.root}
            fileObjects={files}
            accept="text/xml"
            onChange={handleChange}
            acceptedFiles={["text/xml"]}
            showFileNames
            filesLimit={1000}
            maxFileSize={1024 * 1024}
            showAlerts={["error"]}
            showPreviewsInDropzone
            useChipsForPreview
            showFileNamesInPreview
            onDelete={onDeleteChip}
            previewChipProps={{
              size: "small",
              variant: "outlined",
              sx: { marginRight: 100 }
            }}
          />
        </div>
      )}

      <div style={{ height: "20px" }} />

      {/* ACTION BUTTONS */}
      <ArgoButtonHook
        fileName="QuestionnaireImport"
        fieldName="import"
        variant="contained"
        label="IMPORT"
        width={140}
        containedTextColor="white"
        containedBgColor={Colors.blue[500]}
        containedHoverTextColor="#010203"
        containedHoverBgColor={Colors.blue[500]}
        onClick={handleImport}
        disabled={disableImport}
      />

      <div style={{ height: "20px" }} />

      {/* RESULT TABLE */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Filename</TableCell>
              <TableCell>Processed</TableCell>
              <TableCell colSpan={2}>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uploadedFilesReversed.map((result, idx) => {
              const { processedFileName, successfulRun, messages } = result;
              return (
                <TableRow
                  key={idx}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>{processedFileName}</TableCell>
                  <TableCell>{successfulRun ? "Yes" : "No"}</TableCell>
                  <TableCell colSpan={2}>
                    <ul
                      style={{
                        ...batchResultsStyles.list,
                        height: 25,
                        padding: 0,
                        margin: 0
                      }}
                    >
                      {messages.map((m, i) => (
                        <li key={i} style={batchResultsStyles.messageList}>
                          {m}
                        </li>
                      ))}
                    </ul>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <div>&nbsp;</div>

      <div style={{ float: "right", paddingBottom: "20px" }}>
        <ArgoButtonHook
          fileName="QuestionnaireImport"
          fieldName="clear"
          variant="contained"
          label="CLEAR"
          width={140}
          containedTextColor="white"
          containedBgColor={Colors.blue[500]}
          containedHoverTextColor="#010203"
          containedHoverBgColor={Colors.blue[500]}
          onClick={handleClear}
          disabled={uploading || uploadedFiles.length === 0}
        />
      </div>
    </div>
  );
};

export default QuestionnaireImportHook;
